import React from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Chip,
  Box,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { AlertCircle, AlertTriangle } from "lucide-react";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";

export const A11yInspectorDrawer = ({
  open,
  issues = [],
  onClose,
  onSelectIssue,
}) => {
  const t = useScrollytellTranslation();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 380,
          maxWidth: "100%",
          padding: 2,
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 1,
        }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {t("a11yInspectorDrawer.title", "Tilgjengelighetssjekk (WCAG)")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, lineHeight: 1.35 }}
          >
            {t(
              "a11yInspectorDrawer.subtitle",
              "Sjekker tekstkontrast, bildetekster og tilgjengelighet. Klikk på et varsel for å gå direkte til innholdet."
            )}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t("a11yInspectorDrawer.close", "Lukk")}>
          <Close />
        </IconButton>
      </Box>
      <Divider sx={{ my: 1.5 }} />

      {issues.length === 0 ? (
        <Box sx={{ padding: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body1">
            {t("a11yInspectorDrawer.noIssues", "Ingen tilgjengelighetsmerknader funnet. Alt ser bra ut!")}
          </Typography>
        </Box>
      ) : (
        <List sx={{ width: "100%", padding: 0 }}>
          {issues.map((issue) => {
            const isCritical = issue.severity === "critical";

            return (
              <ListItem key={issue.id} disablePadding sx={{ marginBottom: 1.5 }}>
                <ListItemButton
                  onClick={() => onSelectIssue && onSelectIssue(issue.target)}
                  sx={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    border: `1px solid ${isCritical ? "#D32F2F" : "#ED6C02"}`,
                    borderRadius: "8px",
                    padding: 1.5,
                    backgroundColor: isCritical
                      ? "rgba(211, 47, 47, 0.04)"
                      : "rgba(237, 108, 2, 0.04)",
                    "&:hover": {
                      backgroundColor: isCritical
                        ? "rgba(211, 47, 47, 0.1)"
                        : "rgba(237, 108, 2, 0.1)",
                    },
                  }}
                >
                  {issue.target?.sectionIndex !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isCritical ? "#B71C1C" : "#B26A00",
                        display: "inline-block",
                        mb: 0.5,
                      }}
                    >
                      {(() => {
                        const sNum = issue.target.sectionIndex + 1;
                        const rawSec = t("a11yInspectorDrawer.sectionLabel", "Seksjon {{section}}", {
                          section: sNum,
                        });
                        const secLabel = rawSec.includes(String(sNum))
                          ? rawSec
                          : rawSec.replace(/\d+/, String(sNum));

                        if (issue.target.containerIndex !== undefined) {
                          const cNum = issue.target.containerIndex + 1;
                          const rawCont = t("a11yInspectorDrawer.containerLabel", "Tekstboks {{container}}", {
                            container: cNum,
                          });
                          const contLabel = rawCont.includes(String(cNum))
                            ? rawCont
                            : rawCont.replace(/\d+/, String(cNum));
                          return `${secLabel} • ${contLabel}`;
                        }
                        return secLabel;
                      })()}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      marginBottom: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isCritical ? (
                        <AlertCircle size={18} color="#D32F2F" />
                      ) : (
                        <AlertTriangle size={18} color="#ED6C02" />
                      )}
                      <Typography variant="subtitle2" fontWeight="bold">
                        {issue.title}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`WCAG ${issue.wcagCriterion}`}
                      color={isCritical ? "error" : "warning"}
                      variant="outlined"
                      sx={{ fontSize: "11px", height: "20px" }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {issue.description}
                  </Typography>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Drawer>
  );
};
